import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";

function normalizeContactToken(value) {
  return String(value || "").trim().toLowerCase().replace(/[\s-]+/g, "_");
}

function maybeParseJson(value) {
  const source = String(value || "").trim();
  if (!source || !["{", "["].includes(source[0])) return null;

  try {
    return JSON.parse(source);
  } catch {
    return null;
  }
}

function getContactSources(message) {
  const messageType = normalizeContactToken(message?.message_type || message?.type);
  const candidates = [
    message?.contacts,
    message?.contact_cards,
    message?.shared_contacts,
    message?.contact_card,
    messageType === "contacts" ? message?.contact : null,
    messageType === "contacts" ? message?.vcard : null,
  ];
  const sources = [];

  candidates.forEach((candidate) => {
    const parsedCandidate = typeof candidate === "string" ? maybeParseJson(candidate) : candidate;
    const resolvedCandidate = parsedCandidate || candidate;

    if (Array.isArray(resolvedCandidate)) {
      sources.push(...resolvedCandidate);
    } else if (resolvedCandidate && typeof resolvedCandidate === "object") {
      sources.push(resolvedCandidate);
    }
  });

  if (!sources.length && messageType === "contacts") {
    sources.push(message);
  }

  return sources;
}

function firstText(...values) {
  const found = values.find((value) => String(value || "").trim());
  return found ? String(found).trim() : "";
}

function getContactName(rawContact) {
  const name = rawContact?.name;
  const nameObject = name && typeof name === "object" ? name : {};
  const composedName = [
    nameObject.prefix,
    nameObject.first_name,
    nameObject.middle_name,
    nameObject.last_name,
    nameObject.suffix,
  ]
    .filter(Boolean)
    .join(" ");

  return firstText(
    nameObject.formatted_name,
    nameObject.full_name,
    composedName,
    typeof name === "string" ? name : "",
    rawContact?.formatted_name,
    rawContact?.display_name,
    rawContact?.full_name,
    rawContact?.contact_name,
    rawContact?.profile?.name,
    "Contato",
  );
}

function getContactPhone(rawContact) {
  const phoneSources = [
    rawContact?.phones,
    rawContact?.phone_numbers,
    rawContact?.phoneNumbers,
  ]
    .flatMap((value) => (Array.isArray(value) ? value : value ? [value] : []));

  const phoneFromList = phoneSources
    .map((phone) => (
      typeof phone === "object"
        ? firstText(phone.phone, phone.number, phone.value, phone.wa_id, phone.waId)
        : phone
    ))
    .find((phone) => String(phone || "").trim());

  return firstText(
    phoneFromList,
    rawContact?.phone,
    rawContact?.phone_number,
    rawContact?.phoneNumber,
    rawContact?.wa_id,
    rawContact?.waId,
  );
}

export function formatContactPhone(value) {
  const rawValue = String(value || "").trim();
  if (!rawValue) return "";

  if (/[^\d+]/.test(rawValue) && rawValue.replace(/\D/g, "").length < 10) {
    return rawValue;
  }

  const digits = rawValue.replace(/\D/g, "");
  const nationalDigits = digits.startsWith("55") && (digits.length === 12 || digits.length === 13)
    ? digits.slice(2)
    : digits;

  if (nationalDigits.length === 11) {
    return `(${nationalDigits.slice(0, 2)}) ${nationalDigits.slice(2, 7)}-${nationalDigits.slice(7)}`;
  }

  if (nationalDigits.length === 10) {
    return `(${nationalDigits.slice(0, 2)}) ${nationalDigits.slice(2, 6)}-${nationalDigits.slice(6)}`;
  }

  return rawValue;
}

export function getMessageContactCard(message) {
  const rawContact = getContactSources(message)[0];
  if (!rawContact) return null;

  const name = getContactName(rawContact);
  const phone = formatContactPhone(getContactPhone(rawContact));

  return {
    name,
    phone,
  };
}

export function hasMessageContactCard(message) {
  return Boolean(getMessageContactCard(message));
}

function getInitials(name) {
  const words = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (!words.length) return "CT";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return `${words[0][0] || ""}${words[1][0] || ""}`.toUpperCase();
}

function getContactBubbleStyle(theme) {
  const isDark = theme.palette.mode === "dark";

  return {
    backgroundColor: isDark
      ? alpha(theme.palette.common.white, 0.06)
      : "#f1f5f9",
  };
}

export default function TimelineContactCard({
  item,
  reserveTopActionSpace = false,
  timestamp = "",
}) {
  const contactCard = getMessageContactCard(item);
  if (!contactCard) return null;

  return (
    <Box
      sx={(theme) => {
        const bubbleStyle = getContactBubbleStyle(theme);

        return {
          backgroundColor: bubbleStyle.backgroundColor,
          border: 0,
          borderRadius: "8px",
          color: theme.palette.text.primary,
          overflow: "hidden",
          pb: 0.5,
          pl: 1,
          pr: 0.5,
          pt: 1,
          width: { xs: "100%", sm: 332 },
          maxWidth: "100%",
        };
      }}
    >
      <Stack
        alignItems="center"
        direction="row"
        spacing={1.1}
        sx={(theme) => ({
          backgroundColor: theme.palette.background.paper,
          borderRadius: "8px",
          minHeight: 56,
          minWidth: 0,
          px: 1.5,
          py: 1,
          pr: reserveTopActionSpace ? 4 : 1.5,
        })}
      >
        <Avatar
          sx={(theme) => ({
            backgroundColor: theme.palette.mode === "dark"
              ? alpha(theme.palette.common.white, 0.12)
              : "#e2e8f0",
            color: theme.palette.mode === "dark"
              ? theme.palette.text.primary
              : "#475569",
            flexShrink: 0,
            fontSize: 16,
            fontWeight: 400,
            height: 40,
            width: 40,
          })}
        >
          {getInitials(contactCard.name)}
        </Avatar>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              color: "text.primary",
              fontSize: 13,
              fontWeight: 500,
              lineHeight: "20px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={contactCard.name}
          >
            {contactCard.name}
          </Typography>
          {contactCard.phone ? (
            <Typography
              sx={{
                color: "text.secondary",
                fontSize: 12,
                fontWeight: 400,
                lineHeight: "20px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={contactCard.phone}
            >
              {contactCard.phone}
            </Typography>
          ) : null}
        </Box>
      </Stack>

      {timestamp ? (
        <Typography
          sx={{
            color: "text.secondary",
            fontSize: 10,
            fontWeight: 500,
            lineHeight: "15px",
            mt: 1,
            textAlign: "right",
          }}
        >
          {timestamp}
        </Typography>
      ) : null}
    </Box>
  );
}
